/** Semantic UI tokens shared by every active feature. */
const pagePadding = { xs: 1.5, sm: 1.5, lg: 2 } as const;

export const layoutTokens = {
  screenMinHeight: "100dvh",
  pagePadding,
  workspaceMaxWidth: 1680,
  workspacePagePadding: pagePadding,
  sectionGap: { xs: 3, sm: 4, lg: 5 },
  panelPadding: 2,
  controlGap: { xs: 0.75, sm: 1 },
  contentGap: { xs: 1.25, sm: 1.75 },
  structuralBorder: 2,
  dividerBorder: 1,
  controlHeight: 44,
  boardWorkspaceColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 0.75fr)" },
  boardWorkspaceRows: { xs: "minmax(0, 3fr) minmax(0, 2fr)", md: "minmax(0, 1fr)" },
  fittedSquare: "min(100cqw, 100cqh)",
  cardMinHeight: 224,
  readingMaxWidth: 680,
  detailMaxWidth: 560,
  compactMaxWidth: 360,
  historyMaxHeight: 208,
  evidenceRailHeight: 8,
  accentBorder: 3,
  reviewTabMinWidth: 84,
} as const;

export const typographyTokens = {
  tracking: { heading: "-.04em", label: ".08em" },
  lineHeight: { heading: 1.2, body: 1.6 },
} as const;

export const switchTokens = {
  medium: { width: 60, height: layoutTokens.controlHeight, trackWidth: 36, trackHeight: 20, thumb: 12 },
  small: { width: 48, height: 32, trackWidth: 32, trackHeight: 16, thumb: 8 },
} as const;

export const loadingTokens = {
  minHeight: 180,
  padding: 3,
  boardSize: 72,
  boardGap: 2.5,
  animation: { duration: "1.1s", columnDelayMs: 80, dimOpacity: 0.28 },
  title: { fontSize: 18, fontWeight: 950, letterSpacing: typographyTokens.tracking.heading },
  detail: { fontFamily: "monospace", fontSize: 10, letterSpacing: ".1em", mt: 0.75 },
  aside: { fontSize: 10, lineHeight: 1.5, mt: 1.25 },
} as const;

export const DARK_SURFACES = {
  canvas: "#0a0a0a",
  panel: "#101010",
  control: "#101010",
} as const;

export const OFF_WHITE = {
  light: "#f6f3ed",
  main: "#f0ece2",
  dark: "#98968f",
  contrastText: DARK_SURFACES.canvas,
} as const;

export const ALERT_RED = {
  light: "#ff8761",
  main: "#ff3d00",
  dark: "#a12a04",
  contrastText: DARK_SURFACES.canvas,
} as const;

export const SUCCESS_GREEN = {
  light: "#76da9e",
  main: "#22c55e",
  dark: "#1c7f43",
  contrastText: DARK_SURFACES.canvas,
} as const;

export const WARNING_GOLD = {
  light: "#f9c267",
  main: "#f59e0b",
  dark: "#9b660a",
  contrastText: DARK_SURFACES.canvas,
} as const;

export const INFO_BLUE = {
  light: "#849ef9",
  main: "#3b82f6",
  dark: "#2954a0",
  contrastText: DARK_SURFACES.canvas,
} as const;

/** Inverted fill for selected cards and high-priority summaries. */
export const ACTIVE_SURFACE = OFF_WHITE.main;
export const ACTIVE_SURFACE_MUTED = "rgba(10,10,10,0.68)";
export const surfaceTokens = {
  inverseHover: "rgba(10,10,10,0.06)",
  inverseDivider: "rgba(10,10,10,0.22)",
  hover: "rgba(240,236,226,0.06)",
  selected: "rgba(240,236,226,0.1)",
} as const;

/** Board evidence legend: pins must stay distinct from blue support markers. */
export const boardTokens = {
  interaction: {
    reply: "rgba(55, 138, 105, 0.32)",
    selected: "rgba(246, 246, 105, 0.55)",
    quietTarget: "radial-gradient(circle, rgba(20, 20, 20, 0.42) 0 16%, transparent 17%)",
    captureTarget:
      "radial-gradient(circle closest-side, transparent 0 79%, rgba(20, 20, 20, 0.5) 80% 95%, transparent 96% 100%)",
  },
  badgeSize: 30,
  evidenceDisclosure: { inset: 12, minWidth: 160, maxWidth: 280, minHeight: 160, maxHeight: 210 },
  pin: "#8456cc",
  evaluation: {
    gap: 1,
    width: 38,
    gutterWidth: 54,
    height: 22,
    background: "#111",
    divider: "rgba(240,236,226,0.25)",
    verticalFontSize: "0.68rem",
    horizontalFontSize: "0.75rem",
    fontWeight: 900,
    transitionMs: 220,
    /** Phones: a thin, label-free bar so the board keeps the full width. */
    mobileWidth: 4,
    mobileGutterWidth: 6,
  },
} as const;
